import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Business,
  Email,
  Phone,
  LocationOn,
  DocumentScanner,
  Cancel,
  Visibility,
} from '@mui/icons-material';

interface VendorApplication {
  id: string;
  businessName: string;
  ownerName: string;
  email: string;
  phone: string;
  category: string;
  businessType: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  documents: string[];
  aadhaarCard?: string;
  panCard?: string;
  photo?: string;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  businessDetails: {
    description: string;
    website?: string;
    operatingHours: {
      monday: { open: string; close: string; isClosed: boolean };
      tuesday: { open: string; close: string; isClosed: boolean };
      wednesday: { open: string; close: string; isClosed: boolean };
      thursday: { open: string; close: string; isClosed: boolean };
      friday: { open: string; close: string; isClosed: boolean };
      saturday: { open: string; close: string; isClosed: boolean };
      sunday: { open: string; close: string; isClosed: boolean };
    };
  };
  logoUrl?: string;
  images: string[];
  createdAt: string;
  submittedAt: string;
}

const VendorApproval: React.FC = () => {
  const [applications, setApplications] = useState<VendorApplication[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<VendorApplication | null>(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/vendors?status=pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      if (data.success) {
        // Transform backend data to frontend format
        const transformedApplications: VendorApplication[] = data.data.vendors.map((vendor: any) => ({
          id: vendor._id,
          businessName: vendor.businessName,
          ownerName: vendor.ownerName,
          email: vendor.email,
          phone: vendor.phone,
          category: vendor.category?.name || 'Unknown',
          businessType: vendor.businessType,
          status: vendor.status,
          documents: vendor.documents || [],
          aadhaarCard: vendor.aadhaarCard || null,
          panCard: vendor.panCard || null,
          photo: vendor.photo || null,
          location: {
            address: vendor.location?.address || vendor.address?.street || '',
            city: vendor.location?.city || vendor.address?.city || '',
            state: vendor.address?.state || '',
            zipCode: vendor.address?.zipCode || '',
            country: vendor.address?.country || '',
          },
          businessDetails: vendor.businessDetails || {},
          logoUrl: vendor.logoUrl,
          images: vendor.images || [],
          createdAt: vendor.createdAt,
          submittedAt: vendor.createdAt,
        }));
        setApplications(transformedApplications);
      } else {
        // No fallback data - display empty state if API fails
        setApplications([]);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setApplications([]);
    } finally {
      // Loading completed
    }
  };

  const handleApprove = async (application: VendorApplication) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/vendors/${application.id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      if (data.success) {
        setApplications(applications.map(app =>
          app.id === application.id ? { ...app, status: 'approved' } : app
        ));
        setApprovalDialog(false);
        setSelectedApplication(null);
      }
    } catch (error) {
      console.error('Error approving application:', error);
    }
  };

  const handleReject = async (application: VendorApplication, reason: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/vendors/${application.id}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rejectionReason: reason }),
      });
      
      const data = await response.json();
      if (data.success) {
        setApplications(applications.map(app =>
          app.id === application.id ? { ...app, status: 'rejected', rejectionReason: reason } : app
        ));
        setRejectionDialog(false);
        setSelectedApplication(null);
        setRejectionReason('');
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle />;
      case 'pending':
        return <Warning />;
      case 'rejected':
        return <Cancel />;
      default:
        return <Warning />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatOperatingHours = (hours: any) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return days.map(day => {
      const dayHours = hours[day];
      const dayName = day.charAt(0).toUpperCase() + day.slice(1);
      if (dayHours.isClosed) {
        return `${dayName}: Closed`;
      }
      return `${dayName}: ${dayHours.open} - ${dayHours.close}`;
    });
  };


  return (
    <Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        Vendor Approval
      </Typography>
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ 
          mb: { xs: 3, md: 4 },
          fontSize: { xs: '0.875rem', sm: '1rem' },
        }}
      >
        Review and approve vendor applications for platform access.
      </Typography>

      {/* Pending Applications Table */}
      <Card sx={{ 
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
      }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer
            sx={{
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: { xs: '70vh', md: 'none' },
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#555',
                },
              },
            }}
          >
            <Table sx={{ minWidth: { xs: '800px', md: '100%' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem' }}>Business</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem' }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem' }}>Submitted</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.filter(app => app.status === 'pending').map((application) => (
                  <TableRow 
                    key={application.id}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ 
                          mr: 2, 
                          width: 40, 
                          height: 40, 
                          background: 'linear-gradient(135deg, #3B3B3B 0%, #9A8174 100%)',
                        }}>
                          <Business sx={{ fontSize: 20, color: 'white' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#3B3B3B' }}>
                            {application.businessName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Owner: {application.ownerName}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: { xs: '200px', md: 'auto' } }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Email sx={{ mr: 1, fontSize: 14, color: '#3B3B3B', flexShrink: 0 }} />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: { xs: '150px', md: 'none' },
                            }}
                          >
                            {application.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Phone sx={{ mr: 1, fontSize: 14, color: '#3B3B3B', flexShrink: 0 }} />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: { xs: '150px', md: 'none' },
                            }}
                          >
                            {application.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOn sx={{ mr: 1, fontSize: 14, color: '#3B3B3B' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {application.location.city}, {application.location.state}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {application.category}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        icon={getStatusIcon(application.status)}
                        label={application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                        color={getStatusColor(application.status) as any}
                        size="small"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(application.submittedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: { xs: '250px', md: 'auto' } }}>
                      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => {
                            setSelectedApplication(application);
                            setActiveStep(0);
                          }}
                          sx={{
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            borderColor: '#3B3B3B',
                            color: '#3B3B3B',
                            minWidth: 'auto',
                            px: { xs: 1, sm: 1.5 },
                            py: 0.5,
                            '&:hover': {
                              borderColor: '#050206',
                              backgroundColor: 'rgba(59, 59, 59, 0.04)',
                            },
                          }}
                        >
                          Review
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CheckCircle />}
                          onClick={() => {
                            setSelectedApplication(application);
                            setApprovalDialog(true);
                          }}
                          sx={{
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
                            minWidth: 'auto',
                            px: { xs: 1, sm: 1.5 },
                            py: 0.5,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #45a049 0%, #7cb342 100%)',
                            },
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Cancel />}
                          onClick={() => {
                            setSelectedApplication(application);
                            setRejectionDialog(true);
                          }}
                          sx={{
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            background: 'linear-gradient(135deg, #F44336 0%, #E91E63 100%)',
                            minWidth: 'auto',
                            px: { xs: 1, sm: 1.5 },
                            py: 0.5,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #d32f2f 0%, #c2185b 100%)',
                            },
                          }}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Application Review Dialog */}
      <Dialog
        open={Boolean(selectedApplication) && !approvalDialog && !rejectionDialog}
        onClose={() => setSelectedApplication(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Application Review: {selectedApplication?.businessName}
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              <Stepper activeStep={activeStep} orientation="vertical">
                <Step>
                  <StepLabel>Business Information</StepLabel>
                  <StepContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <TextField
                          fullWidth
                          label="Business Name"
                          value={selectedApplication?.businessName || ''}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <TextField
                          fullWidth
                          label="Owner Name"
                          value={selectedApplication?.ownerName || ''}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <TextField
                          fullWidth
                          label="Category"
                          value={selectedApplication?.category || ''}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <TextField
                          fullWidth
                          label="Business Type"
                          value={selectedApplication?.businessType || ''}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 100%' }}>
                        <TextField
                          fullWidth
                          label="Description"
                          value={selectedApplication?.businessDetails?.description || ''}
                          multiline
                          rows={3}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      </Box>
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Contact Details</StepLabel>
                  <StepContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={selectedApplication?.email || ''}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <TextField
                          fullWidth
                          label="Phone"
                          value={selectedApplication?.phone || ''}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 100%' }}>
                        <TextField
                          fullWidth
                          label="Website"
                          value={selectedApplication?.businessDetails?.website || 'N/A'}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      </Box>
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Location & Hours</StepLabel>
                  <StepContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 100%' }}>
                        <TextField
                          fullWidth
                          label="Address"
                          value={`${selectedApplication?.location?.address || ''}, ${selectedApplication?.location?.city || ''}, ${selectedApplication?.location?.state || ''} ${selectedApplication?.location?.zipCode || ''}`}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 100%' }}>
                        <Typography variant="h6" gutterBottom>
                          Operating Hours
                        </Typography>
                        <List sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 0 }}>
                          {selectedApplication?.businessDetails?.operatingHours && formatOperatingHours(selectedApplication.businessDetails.operatingHours).map((hours, index) => (
                            <ListItem key={index} sx={{ borderBottom: index < formatOperatingHours(selectedApplication.businessDetails.operatingHours).length - 1 ? '1px solid #e0e0e0' : 'none', py: 1.5 }}>
                              <ListItemText primary={hours} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                      </Box>
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Documents & Images</StepLabel>
                  <StepContent>
                    <Typography variant="h6" gutterBottom>
                      Required Documents
                    </Typography>
                    <List sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 0, mb: 2 }}>
                      {/* Aadhaar Card */}
                      <ListItem sx={{ borderBottom: '1px solid #e0e0e0', py: 1.5 }}>
                        <ListItemIcon>
                          <DocumentScanner />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Aadhaar Card" 
                          secondary={selectedApplication?.aadhaarCard ? 'Document uploaded' : 'No document uploaded'}
                          primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                        {selectedApplication?.aadhaarCard && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            href={selectedApplication.aadhaarCard}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </Button>
                        )}
                      </ListItem>
                      
                      {/* PAN Card */}
                      <ListItem sx={{ borderBottom: '1px solid #e0e0e0', py: 1.5 }}>
                        <ListItemIcon>
                          <DocumentScanner />
                        </ListItemIcon>
                        <ListItemText 
                          primary="PAN Card" 
                          secondary={selectedApplication?.panCard ? 'Document uploaded' : 'No document uploaded'}
                          primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                        {selectedApplication?.panCard && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            href={selectedApplication.panCard}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </Button>
                        )}
                      </ListItem>
                      
                      {/* Photo */}
                      <ListItem sx={{ borderBottom: 'none', py: 1.5 }}>
                        <ListItemIcon>
                          <DocumentScanner />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Photo" 
                          secondary={selectedApplication?.photo ? 'Photo uploaded' : 'No photo uploaded'}
                          primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                        {selectedApplication?.photo && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            href={selectedApplication.photo}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </Button>
                        )}
                      </ListItem>
                    </List>
                    
                    {/* Additional Documents (if any) */}
                    {selectedApplication?.documents && selectedApplication.documents.length > 0 && (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                          Additional Documents
                        </Typography>
                        <List sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 0, mb: 2 }}>
                          {selectedApplication.documents.map((doc, index) => (
                            <ListItem key={index} sx={{ borderBottom: index < selectedApplication.documents.length - 1 ? '1px solid #e0e0e0' : 'none', py: 1.5 }}>
                              <ListItemIcon>
                                <DocumentScanner />
                              </ListItemIcon>
                              <ListItemText 
                                primary={doc} 
                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                              />
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Visibility />}
                                href={doc}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </Button>
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                    
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Business Images
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {selectedApplication?.images?.map((image, index) => (
                        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }} key={index}>
                          <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption">Image {index + 1}</Typography>
                          </Paper>
                        </Box>
                      ))}
                      </Box>
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Review & Decision</StepLabel>
                  <StepContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Please review all information carefully before making a decision.
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => {
                          setApprovalDialog(true);
                        }}
                      >
                        Approve Application
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => {
                          setRejectionDialog(true);
                        }}
                      >
                        Reject Application
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              </Stepper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedApplication(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)}>
        <DialogTitle>Approve Application</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve the application for "{selectedApplication?.businessName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            onClick={() => selectedApplication && handleApprove(selectedApplication)}
            color="success"
            variant="contained"
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialog} onClose={() => setRejectionDialog(false)}>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Please provide a reason for rejecting the application for "{selectedApplication?.businessName}":
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
          <Button
            onClick={() => selectedApplication && handleReject(selectedApplication, rejectionReason)}
            color="error"
            variant="contained"
            disabled={!rejectionReason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorApproval;
